import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2, Sparkles, MessageSquare } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { OFFICERS, OfficerBadge } from "./AIOfficers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * Interactive AI Officer chat panel.
 * Opens as a right-side slide-over when an officer is activated.
 * Carries conversation history per session, with suggested starter prompts.
 */
export const OfficerChat = ({ officerId, profileId, profileName, open, onClose }) => {
  const officer = OFFICERS.find((o) => o.id === officerId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const scrollRef = useRef(null);

  // Reset conversation when officer or profile changes
  useEffect(() => {
    setMessages([]);
    setSessionId(null);
    setInput("");
  }, [officerId, profileId]);

  // Load suggestions for this officer
  useEffect(() => {
    if (!open || !officerId) return;
    axios.get(`${API}/political/officers/suggestions/${officerId}`).then((r) => {
      setSuggestions(r.data?.suggestions || []);
    }).catch(() => setSuggestions([]));
  }, [open, officerId]);

  // Auto-scroll on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const sendQuestion = async (question) => {
    const q = (question || input).trim();
    if (!q || sending) return;
    setInput("");
    const userMsg = { role: "user", content: q, ts: Date.now() };
    setMessages((prev) => [...prev, userMsg]);
    setSending(true);

    try {
      const res = await axios.post(
        `${API}/political/intel/${profileId}/ask`,
        {
          officer_id: officerId,
          question: q,
          history: messages.slice(-4),
          session_id: sessionId,
        },
        { timeout: 60000 }
      );
      const { answer, session_id, latency_ms } = res.data;
      setSessionId(session_id);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: answer, ts: Date.now(), latency_ms },
      ]);
    } catch (e) {
      const errorMsg = e?.response?.data?.detail || "El officer no esta disponible en este momento.";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Error: ${errorMsg}`, ts: Date.now(), error: true },
      ]);
    }
    setSending(false);
  };

  if (!officer) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            data-testid="officer-chat-backdrop"
          />
          {/* Slide-over panel */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 240 }}
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[480px] lg:w-[560px] bg-[#0A0A0F] border-l border-white/10 z-50 flex flex-col shadow-2xl"
            data-testid={`officer-chat-panel-${officerId}`}
          >
            {/* Header */}
            <div className="p-5 border-b border-white/[0.08] flex items-start gap-4">
              <OfficerBadge officer={officer} active size="lg" />
              <div className="flex-1 min-w-0">
                <span className="text-[9px] uppercase tracking-widest text-emerald-400 font-medium">EN LINEA</span>
                <h2 className="text-base font-bold text-white">{officer.name}</h2>
                <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">{officer.specialty}</p>
                {profileName && (
                  <p className="text-[10px] text-purple-300 mt-1.5 uppercase tracking-widest">Consultando: {profileName}</p>
                )}
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors flex-shrink-0"
                data-testid="officer-chat-close"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto p-5 space-y-4 custom-scroll"
              data-testid="officer-chat-messages"
            >
              {messages.length === 0 && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 rounded-2xl bg-purple-500/5 border border-purple-500/15">
                    <Sparkles className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-gray-300 leading-relaxed">
                      Hola. Soy <span className="text-white font-semibold">{officer.name}</span>. Tengo acceso al contexto completo del perfil monitoreado. Preguntame sobre {officer.specialty}.
                    </div>
                  </div>
                  {suggestions.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-gray-500 font-medium mb-2">Sugerencias para empezar</p>
                      <div className="space-y-2">
                        {suggestions.map((s, i) => (
                          <button
                            key={`sugg-${s?.slice(0, 30) || i}`}
                            onClick={() => sendQuestion(s)}
                            disabled={sending}
                            className="w-full text-left p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-purple-500/30 hover:bg-white/[0.04] transition-colors text-xs text-gray-300 disabled:opacity-50"
                            data-testid={`suggestion-${i}`}
                          >
                            <MessageSquare className="w-3 h-3 inline-block mr-2 text-purple-400" />
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {messages.map((m, i) => (
                <MessageBubble key={m.id || `msg-${i}-${m.role}`} message={m} officer={officer} />
              ))}

              {sending && (
                <div className="flex items-start gap-3">
                  <OfficerBadge officer={officer} size="sm" />
                  <div className="flex-1 p-3 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-purple-400" />
                      Procesando con IA...
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-4 border-t border-white/[0.08] bg-[#08080C]">
              <div className="flex gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendQuestion();
                    }
                  }}
                  placeholder={`Pregunta a ${officer.title}...`}
                  rows={2}
                  maxLength={1000}
                  className="flex-1 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-purple-500/40 resize-none"
                  disabled={sending}
                  data-testid="officer-chat-input"
                />
                <button
                  onClick={() => sendQuestion()}
                  disabled={sending || !input.trim()}
                  className="px-4 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 text-white text-xs font-medium hover:opacity-90 disabled:opacity-30 transition-opacity flex items-center justify-center"
                  data-testid="officer-chat-send"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[10px] text-gray-600 mt-2 px-1">
                Las respuestas pueden contener imprecisiones. Validar antes de tomar decisiones criticas.
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const MessageBubble = ({ message, officer }) => {
  const isUser = message.role === "user";
  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] px-3.5 py-2.5 rounded-2xl bg-purple-500/15 border border-purple-500/25 text-white text-sm leading-relaxed">
          {message.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3">
      <OfficerBadge officer={officer} size="sm" />
      <div className={`flex-1 p-3.5 rounded-2xl ${message.error ? "bg-red-500/10 border border-red-500/30" : "bg-white/[0.03] border border-white/[0.06]"}`}>
        <div className="prose prose-invert prose-sm max-w-none text-gray-200 leading-relaxed text-sm">
          <ReactMarkdown>{message.content}</ReactMarkdown>
        </div>
        {message.latency_ms && (
          <p className="text-[10px] text-gray-600 mt-2 pt-2 border-t border-white/5">
            Generado en {(message.latency_ms / 1000).toFixed(1)}s
          </p>
        )}
      </div>
    </div>
  );
};

export default OfficerChat;
